import { z } from 'zod';

/**
 * Trust and safety: removals, appeals, copyright notices, blocks and the review queue.
 * See apps/api/src/lib/safety.ts and docs/operations/trust-and-safety.md.
 */

/** Why a stream was removed. Ids are stored (streams.takedown_reason); labels are shown. */
export const REMOVAL_REASONS = [
  { id: 'violence', label: 'Violence' },
  { id: 'nudity', label: 'Nudity or sexual content' },
  { id: 'gambling', label: 'Gambling' },
  { id: 'harassment', label: 'Harassment or hate' },
  { id: 'spam', label: 'Spam or scams' },
  { id: 'copyright', label: 'Copyright' },
  { id: 'other', label: 'Breaking our terms' },
] as const;
export type RemovalReason = (typeof REMOVAL_REASONS)[number]['id'];
export const REMOVAL_REASON_IDS = REMOVAL_REASONS.map((r) => r.id) as [RemovalReason, ...RemovalReason[]];

export function removalLabel(reason: RemovalReason | null | undefined): string {
  return REMOVAL_REASONS.find((r) => r.id === reason)?.label ?? 'Breaking our terms';
}

/**
 * The reason behind a stored takedown: `auto:<rule>:<policy>` (the AI), `staff:<reason>` (the review
 * queue) or `copyright:<notice id>`. Anything else (a takedown done by hand) is "other".
 */
export function removalReasonOf(takedownReason: string | null | undefined): RemovalReason {
  const [kind, a, b] = (takedownReason ?? '').split(':');
  const id = kind === 'copyright' ? 'copyright' : kind === 'staff' ? a : kind === 'auto' ? b : null;
  return (REMOVAL_REASON_IDS as string[]).includes(id ?? '') ? (id as RemovalReason) : 'other';
}

// ---- The rules people agree to ----

/**
 * The terms everyone agrees to before they first go live or chat in the app (guests too). Bump it
 * when the rules change: everyone is asked again. The website's /terms page says the same.
 */
export const TERMS_VERSION = '2026-10-21';
/** Nobody younger uses ZIP2LIVE. */
export const MIN_AGE = 13;
/** Going live is for adults. Teens can watch and chat. */
export const MIN_BROADCAST_AGE = 18;

/** What ZIP2LIVE has zero tolerance for, in the words the agree screen and the terms use. */
export const BANNED_CONTENT = [
  'Sex, nudity or sexual content',
  'Anything sexual involving someone under 18',
  'Intimate images of someone without their consent',
  'Violence, gore or cruelty, including to animals',
  'Threats, harassment, bullying or hate',
  'Encouraging self-harm or suicide',
  'Illegal activity, drugs or weapons sales, or gambling',
  'Streaming someone else’s work without permission',
] as const;

/** As the person declared it: 13 to 17, or 18 and over. */
export const AgeGroup = z.enum(['teen', 'adult']);
export type AgeGroup = z.infer<typeof AgeGroup>;

/** Where the age came from: the person's own answer, or their Apple or Google Play account. */
export const AgeSource = z.enum(['self', 'apple', 'google']);
export type AgeSource = z.infer<typeof AgeSource>;

export const AgreeTermsRequest = z.object({
  version: z.literal(TERMS_VERSION),
  ageGroup: AgeGroup,
  ageSource: AgeSource.default('self'),
  source: z.enum(['mobile', 'web']).default('web'),
});

export interface TermsAgreement {
  version: string;
  ageGroup: AgeGroup;
  ageSource: AgeSource;
  acceptedAt: number;
}

/**
 * The age group for an age range from the phone's store account (Apple's Declared Age Range, or
 * Google Play Age Signals: 0-12, 13-15, 16-17, 18+). 'under': younger than MIN_AGE. null: the range
 * doesn't say (ask the person instead).
 */
export function ageGroupFromRange(lower: number | null | undefined, upper: number | null | undefined): AgeGroup | 'under' | null {
  const lo = lower ?? null;
  const hi = upper ?? null;
  if (lo !== null && lo >= MIN_BROADCAST_AGE) return 'adult';
  if (hi !== null && hi < MIN_AGE) return 'under';
  // 13 or over but maybe not 18: treated as a teen until a range says otherwise.
  if (lo !== null && lo >= MIN_AGE) return 'teen';
  return null;
}

/** Age in whole years on `today` for someone born in `month` (1–12) of `year`, counting from the end of that month. */
export function ageFrom(year: number, month: number, today = new Date()): number {
  const age = today.getFullYear() - year;
  return today.getMonth() + 1 > month ? age : age - 1;
}

/** Typed by the person to confirm deleting their account. */
export const DELETE_ACCOUNT_CONFIRM = 'DELETE';
export const DeleteAccountRequest = z.object({ confirm: z.literal(DELETE_ACCOUNT_CONFIRM) });

/** Block whoever sent a chat message (seq), or the creator of a stream (no seq). */
export const BlockRequest = z.object({
  streamId: z.string().max(40),
  seq: z.number().int().positive().optional(),
});

export interface Block {
  /** The blocked person's chat key: their messages carry it. */
  key: string;
  name: string;
  createdAt: number;
}

export type AppealStatus = 'pending' | 'accepted' | 'denied';

export interface AppealState {
  status: AppealStatus;
  createdAt: number;
  decidedAt: number | null;
  /** What staff said when deciding, if anything. */
  note: string | null;
}

/** What a creator sees about their own removed streams and their account. */
export interface SafetyStatus {
  /** Can open the review queue. */
  staff: boolean;
  suspension: { reason: string; since: number; appeal: AppealState | null } | null;
  removals: {
    streamId: string;
    reason: RemovalReason;
    removedAt: number;
    /** A person looked at it (the AI removes some streams on its own). */
    reviewed: boolean;
    appeal: AppealState | null;
  }[];
}

/** A copyright counter-notice: what 17 U.S.C. 512(g)(3) asks for, next to the appeal message. */
export const CounterNotice = z.object({
  name: z.string().trim().min(2).max(120),
  address: z.string().trim().min(5).max(400),
  phone: z.string().trim().max(40).optional(),
  /** Good-faith belief the removal was a mistake or misidentification, under penalty of perjury. */
  mistake: z.literal(true),
  /** Consent to the jurisdiction of the federal court for their address, and to accept service. */
  jurisdiction: z.literal(true),
  signature: z.string().trim().min(2).max(120),
});
export type CounterNotice = z.infer<typeof CounterNotice>;

export const AppealRequest = z.object({
  /** The removed stream; leave out to appeal a suspension. */
  streamId: z.string().max(40).optional(),
  message: z.string().trim().min(10).max(2000),
  counterNotice: CounterNotice.optional(),
});

/** A notice from a rights holder (or their agent), about one stream. Fields follow 17 U.S.C. 512(c)(3). */
export const CopyrightNoticeRequest = z.object({
  /** zip2live.com/s/<id> link, or the id. */
  stream: z.string().trim().min(4).max(300),
  name: z.string().trim().min(2).max(120),
  email: z.email().max(254),
  address: z.string().trim().max(400).optional(),
  /** The rights holder, if you are filing for someone else. */
  onBehalfOf: z.string().trim().max(200).optional(),
  work: z.string().trim().min(5).max(2000),
  originalUrl: z.string().trim().max(500).optional(),
  infringement: z.string().trim().max(2000).optional(),
  /** Good-faith belief the use is not authorized by the owner, its agent or the law. */
  goodFaith: z.literal(true),
  /** The notice is accurate and, under penalty of perjury, the filer may act for the owner. */
  accurate: z.literal(true),
  signature: z.string().trim().min(2).max(120),
});
export type CopyrightNoticeRequest = z.infer<typeof CopyrightNoticeRequest>;

/** The stream id in a pasted link (zip2live.com/s/<id>, with or without more after it) or a bare id. */
export function streamIdFrom(text: string): string | null {
  const t = text.trim();
  const m = /\/s\/([a-z0-9]{4,16})(?:[/?#]|$)/.exec(t) ?? /^([a-z0-9]{4,16})$/.exec(t);
  return m ? m[1]! : null;
}

// ---- Review queue (staff) ----

export const StreamDecision = z.object({
  action: z.enum(['remove', 'keep', 'restore']),
  reason: z.enum(REMOVAL_REASON_IDS).optional(),
  note: z.string().trim().max(1000).optional(),
});
export const ChatDecision = z.object({
  action: z.enum(['remove', 'dismiss']),
  note: z.string().trim().max(1000).optional(),
});
export const NoticeDecision = z.object({
  action: z.enum(['remove', 'reject']),
  note: z.string().trim().max(1000).optional(),
});
export const AppealDecision = z.object({
  action: z.enum(['accept', 'deny']),
  note: z.string().trim().max(1000).optional(),
});
export const SuspendRequest = z.object({
  reason: z.string().trim().min(3).max(300),
});

/** A person as staff see them in the queue. */
export interface AdminPerson {
  id: string;
  /** @handle, display name, "Guest" or "Deleted account". */
  name: string;
  verified: boolean;
  suspended: boolean;
}

export interface AdminReview {
  id: string;
  kind: 'report' | 'scan';
  status: string;
  /** The policy the AI scored highest, and how sure it was (0..1). */
  top: string | null;
  confidence: number | null;
  scores: Record<string, number> | null;
  /** The clip the AI watched can be played (GET /api/v1/admin/clips/:id). */
  clip: boolean;
  at: number;
}

export interface AdminAppeal {
  id: string;
  kind: 'stream' | 'suspension';
  targetId: string;
  message: string;
  counterNotice: (CounterNotice & { name: string }) | null;
  status: AppealStatus;
  createdAt: number;
  decidedAt: number | null;
  note: string | null;
  /** Who appealed (null after they deleted their account). */
  by: AdminPerson | null;
}

export interface AdminStream {
  id: string;
  title: string | null;
  status: string;
  source: string;
  visibility: string;
  createdAt: number;
  liveAt: number | null;
  endedAt: number | null;
  watchUrl: string;
  /** Staff can watch even a removed or private replay (from wherever it was moved to). */
  playbackUrl: string | null;
  deleted: boolean;
  removedAt: number | null;
  removalReason: RemovalReason | null;
  /** Raw takedown reason, e.g. auto:strikes:nudity. */
  takedownReason: string | null;
  reviewed: boolean;
  restoredAt: number | null;
  peakViewers: number;
  views: number;
  channel: { handle: string | null; displayName: string | null };
  owner: (AdminPerson & { removals: number; copyrightRemovals: number }) | null;
  /** Viewer reports by reason (open: not yet reviewed). */
  reports: { reason: string; count: number; open: number; last: number }[];
  /** AI reviews, most suspicious first. */
  reviews: AdminReview[];
  appeal: AdminAppeal | null;
  copyrightNotices: number;
}

export interface AdminChatReport {
  streamId: string;
  seq: number;
  author: AdminPerson | null;
  authorName: string;
  text: string;
  reasons: string[];
  reporters: number;
  /** Enough reporters hid it for everyone until a decision. */
  hidden: boolean;
  last: number;
  stream: { title: string | null; channel: string | null };
}

export interface AdminNotice {
  id: string;
  streamId: string;
  status: 'pending' | 'removed' | 'rejected';
  claimant: { name: string; email: string; address: string | null; onBehalfOf: string | null };
  work: string;
  originalUrl: string | null;
  infringement: string | null;
  signature: string;
  createdAt: number;
  reviewedAt: number | null;
  note: string | null;
  stream: AdminStream | null;
}

export interface AdminSuspension {
  person: AdminPerson;
  reason: string;
  since: number;
  by: string;
  appeal: AdminAppeal | null;
}

export interface AdminAction {
  id: string;
  actor: string;
  targetType: string;
  targetId: string;
  action: string;
  reason: string | null;
  note: string | null;
  at: number;
}

export interface AdminCounts {
  streams: number;
  chat: number;
  copyright: number;
  appeals: number;
}
